import React, { useEffect, useRef, useState } from "react";
import Auth from "../../../../Auth/Auth";
import ElementService, { ElementContract } from "../../../../services/ElementsService";
import FinametrixService, { IClientDocs } from "../../../../services/FinametrixService";
import AttachmentService from "../../../../services/AttachmentService";
import DocumentationTable, { UnavailableMessage } from "./DocumentationTable";
import { DefaultButton, DetailsList, DetailsListLayoutMode, Modal, Pivot, PivotItem, SelectionMode, Spinner, SpinnerSize, ThemeProvider } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import styles from "../index.module.sass";

const TabComponent = () =>
{
    const firstTimeClientDocuments = useRef<boolean>(false);
    const firstTimeReports = useRef<boolean>(false);
    const firstTimeLoadingElements = useRef<boolean>(false);
    const isLoading = useRef<boolean>(true);
    const [clientDocuments, setClientDocuments] = useState<IClientDocs[]>();
    const [elementContracts, setElementContracts] = useState<ElementContract[]>();
    const [reports, setReports] = useState<any[]>();
    const [clientDocumentsUnavailable, setClientDocumentsUnavailable] = useState<boolean>(false);
    const [reportsUnavailable, setReportsUnavailable] = useState<boolean>(false);
    const [elementContractsUnavailable, setElementContractsUnavailable] = useState<boolean>(false);
    const [elementsDownloadFailed, setElementsDownloadFailed] = useState<boolean>(false);
    const user = Auth.getUserProfile();
    const plan = Auth.getActivePlan();
    const planId = plan?.id;
    const savingsPlanElementsStatus = Auth.getSavingsPlanElementsStatus();
    const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);

    // La pestaña se muestra siempre que el plan tenga contratos de Elements firmados, aunque ya haya cancelado la
    // suscripción. Si la carga falla, solo se muestra (con el aviso) cuando el plan está suscrito y por tanto los tiene.
    const hasElementContracts = (elementContracts?.length ?? 0) > 0;
    const showElementsTab = hasElementContracts || (elementContractsUnavailable && savingsPlanElementsStatus.isSubscribed);

    const dismissModal = () =>
    {
        hideModal();
        setElementsDownloadFailed(false);
    };

    var columns = ["Tipo", "Fecha", "Archivo"];
    var elementsColumns =
    [
        {
            key: "name",
            fieldName: "name",
            name: "Contrato",
            minWidth: 100
        },
        {
            key: "document",
            fieldName: "userIdentityDocument",
            name: "Documento",
            minWidth: 100
        },
        {
            key: "fullname",
            fieldName: "userIdentityFullname",
            name: "Interviniente",
            minWidth: 100
        },
        {
            key: "version",
            fieldName: "version",
            name: "Versión",
            minWidth: 100
        },
        {
            key: "date",
            fieldName: "date",
            name: "Fecha de firma",
            minWidth: 100
        },
        {
            key: "file",
            fieldName: "file",
            name: "Archivo",
            minWidth: 100
        }
    ];

    const renderOrderItemColumn = (item: any, index: any, column: any) =>
    {
        switch(column.key)
        {
            case "file":
                return(
                    <React.Fragment>
                        <a href={'#' + item.file} onClick={(event) =>
                        {
                            event.preventDefault();
                            setElementsDownloadFailed(false);
                            showModal();
                            AttachmentService.downloadIroniaStoreBase64Pdf(event, item).then(() =>
                            {
                                hideModal();
                            })
                            .catch((error) =>
                            {
                                // Antes el modal se cerraba sin avisar y la promesa quedaba rechazada sin capturar
                                console.log(error);
                                setElementsDownloadFailed(true);
                            });
                        }}>
                            <i className={'ironia-icon downloads'} /> Descargar
                        </a>
                    </React.Fragment>
                );

            default:
                return <span>{item[column.key]}</span>
        }
    }

    useEffect(() =>
    {
        if(firstTimeLoadingElements.current === false)
        {
            firstTimeLoadingElements.current = true;

            // Se piden siempre, no solo con la suscripción activa: un plan que canceló Elements sigue viendo lo que firmó
            if(planId !== undefined && planId !== null)
            {
                ElementService.getAllContracts(planId).then((response: ElementContract[]) =>
                {
                    setElementContracts(response);
                })
                .catch((error) =>
                {
                    console.log(error);
                    setElementContractsUnavailable(true);
                });
            }
        }
    }, [planId, user]);

    useEffect(() =>
    {
        if(firstTimeClientDocuments.current === false)
        {
            firstTimeClientDocuments.current = true;

            FinametrixService.getClientDocuments(user.id).then((response: IClientDocs[]) =>
            {
                setClientDocuments(response);
            })
            .catch((error) =>
            {
                // Un error del backend muestra "La información no está disponible" en lugar de una tabla vacía
                console.log(error);
                setClientDocumentsUnavailable(true);
            });
        }
        if(firstTimeReports.current === false)
        {
            firstTimeReports.current = true;
            FinametrixService.getMonthlyReports(user.id).then((response: IClientDocs[]) =>
            {
                setReports(response);
            })
            .catch((error) =>
            {
                console.log(error);
                setReportsUnavailable(true);
            });
        }

        isLoading.current = false;

    }, [firstTimeClientDocuments, firstTimeReports, user]);

    if(isLoading.current === true)
    {
        return(<Spinner size={SpinnerSize.large} />)
    }
    else
    {
        return(
            <React.Fragment>
                <ThemeProvider>
                    <div style={{padding: "10px"}}>
                        <Pivot>
                            <PivotItem headerText="Documentos de Clientes">
                                <DocumentationTable
                                    rowData={clientDocuments}
                                    unavailable={clientDocumentsUnavailable}
                                    columnTitles={["Documento de identificación", ...columns]}
                                    dataType="clientDocument"
                                />
                            </PivotItem>
                            <PivotItem headerText="Reportes Mensuales">
                                <DocumentationTable
                                    rowData={reports}
                                    unavailable={reportsUnavailable}
                                    columnTitles={["Documento de identificación", ...columns]}
                                    dataType="monthlyReport"
                                />
                            </PivotItem>
                            {showElementsTab &&
                            (
                                <PivotItem headerText="Contratos de Elements">
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-md2 ms-hiddenLgDown"></div>
                                        <div className="ms-Grid-col ms-sm12 ms-xl8">
                                            {elementContractsUnavailable === true &&
                                            (
                                                <p>{UnavailableMessage}</p>
                                            )}
                                            {elementContractsUnavailable === false &&
                                            (
                                                <DetailsList
                                                    className={styles.elementsContracts}
                                                    items={elementContracts || []}
                                                    columns={elementsColumns}
                                                    selectionMode={SelectionMode.none}
                                                    onRenderItemColumn={renderOrderItemColumn}
                                                    layoutMode={DetailsListLayoutMode.justified}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </PivotItem>
                            )}
                        </Pivot>
                    </div>
                    {isModalOpen &&
                    (
                        <Modal isOpen={isModalOpen}
                            onDismiss={dismissModal}
                            isBlocking={false}
                            containerClassName={styles.modal}
                        >
                            {elementsDownloadFailed === false &&
                            (
                                <React.Fragment>
                                    <h3>Descargando archivo</h3>
                                    <Spinner size={SpinnerSize.large} />
                                </React.Fragment>
                            )}
                            {elementsDownloadFailed === true &&
                            (
                                <React.Fragment>
                                    <h3>{UnavailableMessage}</h3>
                                    <DefaultButton text="Cerrar" onClick={dismissModal} />
                                </React.Fragment>
                            )}
                        </Modal>
                    )}
                </ThemeProvider>
            </React.Fragment>
        );
    }
}

export default TabComponent;